// ABOUTME: Renders file and media values as DS cards: type icon, file name over field label, language and date.
// ABOUTME: Each card is a link opening its file in a new tab; used by node-info with fileDisplay "card".

import { Card, CardHeader, Link, Stack, Typography } from '@ds/coolshop';
import { resolveImageUri } from '@src/_new/utils/resolveImageUri';

import { resolveIcon } from '../../utils/resolveIcon';
import { fileExtension, fileTypeIconName } from '../utils/fileTypeIcon';

/** Space between two file cards, also between the card lists of two fields. */
export const FILE_CARD_GAP = 1.5;

export interface FileCardItem {
    id: string;
    fieldLabel: string;
    name: string;
    mimeType?: string;
    /** Display name of the language the file is stored in. */
    language?: string;
    /** Formatted date of the file's last change. */
    date?: string;
}

// Language and date stacked on the right, as in the resource cards; the extension stands in
// when the file carries neither.
const renderEndAdornment = (file: FileCardItem) => {
    if (!file.language && !file.date) {
        return (
            <Typography variant="caption" color="text.secondary">
                {fileExtension(file.name)}
            </Typography>
        );
    }
    return (
        <Stack alignItems="flex-end">
            {file.language && (
                <Typography variant="caption" color="text.secondary">
                    {file.language}
                </Typography>
            )}
            {file.date && (
                <Typography variant="caption" color="text.secondary">
                    {file.date}
                </Typography>
            )}
        </Stack>
    );
};

export function FileCardList({
    files,
    showLabel = true,
}: {
    files: FileCardItem[];
    /** Shows the field label under the file name, as `showLabel` does for the other node-info rows. */
    showLabel?: boolean;
}) {
    if (files.length === 0) return null;
    return (
        <Stack gap={FILE_CARD_GAP}>
            {files.map(file => (
                // A DS link around the card: reachable by keyboard and announced as a link, with
                // the card's own look and no hover overlay.
                <Link
                    key={`${file.fieldLabel}-${file.language}-${file.id}`}
                    href={resolveImageUri(file.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="inherit"
                    underline="none"
                >
                    <Card elevation={2} cursor="pointer">
                        <CardHeader
                            // The file name leads; the field it comes from sits under it.
                            title={
                                // minWidth 0 lets a long unbroken file name wrap instead of widening the card.
                                <Stack minWidth={0}>
                                    <Typography variant="body2" wordBreak="break-word">
                                        {file.name}
                                    </Typography>
                                    {showLabel && (
                                        <Typography variant="caption" color="text.secondary">
                                            {file.fieldLabel}
                                        </Typography>
                                    )}
                                </Stack>
                            }
                            AvatarProps={{
                                children: resolveIcon(fileTypeIconName(file)),
                                color: 'secondary',
                            }}
                            endAdornment={renderEndAdornment(file)}
                        />
                    </Card>
                </Link>
            ))}
        </Stack>
    );
}
